/**
 * newsletter-klaviyo.js — intercepts <form data-klaviyo-newsletter> submits,
 * subscribes the profile via Klaviyo's Client API, and calls klaviyo.identify()
 * so the anonymous browser cookie is tied to the profile (enables Browse
 * Abandonment, Abandoned Cart, etc. flows).
 *
 * Configured by data attributes on the form element:
 *   data-klaviyo-newsletter            (presence flag — required)
 *   data-klaviyo-public-key="XXXXXX"   Klaviyo public API key (company_id)
 *   data-klaviyo-list-id-pl="YYYYYY"   PL list ID
 *   data-klaviyo-list-id-en="ZZZZZZ"   EN list ID
 *   data-locale="pl"|"en"              picks which list to use, and is written
 *                                      verbatim to the `Preferred Language`
 *                                      custom property, which the marketing
 *                                      flows branch on. Bare language code,
 *                                      lowercase, no country.
 *   data-klaviyo-locale="pl-PL"        full language-COUNTRY code. Written to
 *                                      BOTH the profile's native `locale` field
 *                                      (via /client/profiles/, on creation) and
 *                                      the `initial-locale` custom property (via
 *                                      identify, as a first-touch record).
 *                                      Distinct from data-locale, and it does
 *                                      NOT affect which list is used.
 *   data-custom-source="..."           optional, included in Klaviyo event
 *   data-msg-name-missing="..."        localized validation messages
 *   data-msg-email-missing="..."         (all optional — JS has en/pl fallbacks)
 *   data-msg-email-invalid="..."
 *   data-msg-consent-missing="..."
 *   data-msg-submit-failed="..."
 *
 * The form should have `novalidate` so the browser's native validation tooltip
 * doesn't fire — this script does field-level validation in submit order and
 * surfaces messages in the [data-klaviyo-error] block instead.
 *
 * Spam mitigations:
 *   - Honeypot: <input name="website"> is invisible; if a bot fills it,
 *     submission is silently dropped.
 *   - Time guard: refuses submissions faster than MIN_TIME_ON_PAGE_MS
 *     (humans can't fill the form that fast; bots can).
 *
 * Form HTML expectations:
 *   - Email field with name="contact[email]" or name="email"
 *   - First-name field with name="contact[first_name]" or name="first_name"
 *   - Optional consent checkbox with name="consent" (required by HTML5)
 *   - Optional honeypot <input name="website">
 *   - Success block with [data-klaviyo-success] hidden by default
 *   - Error block with [data-klaviyo-error] hidden by default; contains
 *     [data-klaviyo-error-text] span whose textContent gets the message
 *   - Submit button (auto-disabled while request is in flight)
 *   - Field wrapper with class .newsletter-form__field-wrapper (hidden on success)
 *
 * No external dependencies. Vanilla ES2017+.
 */

(function () {
  'use strict';

  var KLAVIYO_API = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_PROFILES_API = 'https://a.klaviyo.com/client/profiles/';
  var KLAVIYO_API_REVISION = '2026-04-15';
  var MIN_TIME_ON_PAGE_MS = 3000;
  // Custom property holding the locale the subscriber signed up in. Kept
  // separate from Klaviyo's native `locale` field, which can be rewritten later.
  var INITIAL_LOCALE_PROP = 'initial-locale';
  // Custom property the marketing flows branch on. Nothing else writes to it —
  // unlike the native `locale` field, which Klaviyo's Shopify sync rewrites
  // roughly hourly for anyone who browses the store.
  //
  // The exact spelling matters and fails silently: Klaviyo creates a NEW,
  // separate property for any variation ("preferred_language", "Preferred
  // language"), raises no error, and the flows simply never match. Held in a
  // constant so the four write sites cannot drift apart.
  var PREFERRED_LANGUAGE_PROP = 'Preferred Language';

  var loadTime = Date.now();

  // Manual-testing aid: set window.MALUA_NL_DEBUG = true in the console (or add
  // ?nl_debug=1 to the URL) to trace every step of a submission. Off by default.
  function debugLog() {
    var on = window.MALUA_NL_DEBUG === true ||
             (window.location && window.location.search.indexOf('nl_debug=1') !== -1);
    if (!on) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[klaviyo-newsletter]');
    console.log.apply(console, args);
  }

  function init() {
    var forms = document.querySelectorAll('form[data-klaviyo-newsletter]');
    for (var i = 0; i < forms.length; i++) setupForm(forms[i]);
  }

  function setupForm(form) {
    var publicKey = form.dataset.klaviyoPublicKey;
    var listIdPl  = form.dataset.klaviyoListIdPl;
    var listIdEn  = form.dataset.klaviyoListIdEn;
    var locale    = (form.dataset.locale || 'en').toLowerCase();
    var listId    = locale === 'pl' ? listIdPl : listIdEn;
    var source    = (form.dataset.customSource || 'Newsletter') + ' — ' + locale.toUpperCase();

    // Klaviyo's native `locale` profile field. Klaviyo upserts: a supplied field
    // overwrites, an omitted one is left untouched. So a malformed value would
    // clobber a good one — drop anything that isn't exactly xx or xx-XX rather
    // than sending it (guards against e.g. "pl-PL-PL" or a blank country).
    var klaviyoLocale = (form.dataset.klaviyoLocale || '').trim();
    if (klaviyoLocale && !/^[a-z]{2}(-[A-Z]{2})?$/.test(klaviyoLocale)) {
      console.warn('[klaviyo-newsletter] Ignoring malformed locale "%s"', klaviyoLocale);
      klaviyoLocale = '';
    }

    var successEl     = form.querySelector('[data-klaviyo-success]');
    var errorEl       = form.querySelector('[data-klaviyo-error]');
    var errorTextEl   = form.querySelector('[data-klaviyo-error-text]');
    var fieldWrapper  = form.querySelector('.newsletter-form__field-wrapper');
    var nameInput     = form.querySelector('input[name="contact[first_name]"], input[name="first_name"]');
    var emailInput    = form.querySelector('input[type="email"]');
    var consentInput  = form.querySelector('input[name="consent"]');

    // Localized validation messages — read from form data-* with PL/EN fallbacks
    var msgs = {
      nameMissing:    form.dataset.msgNameMissing    || (locale === 'pl' ? 'Wpisz swoje imię.' : 'Please enter your first name.'),
      emailMissing:   form.dataset.msgEmailMissing   || (locale === 'pl' ? 'Podaj swój adres e-mail.' : 'Please enter your email address.'),
      emailInvalid:   form.dataset.msgEmailInvalid   || (locale === 'pl' ? 'Wpisz prawidłowy adres e-mail.' : 'Please enter a valid email address.'),
      consentMissing: form.dataset.msgConsentMissing || (locale === 'pl' ? 'Zaznacz zgodę, aby kontynuować.' : 'Please tick the consent box to continue.'),
      submitFailed:   form.dataset.msgSubmitFailed   || (locale === 'pl' ? 'Coś poszło nie tak. Spróbuj jeszcze raz.' : 'Something went wrong. Please try again.')
    };

    if (!publicKey || !listId) {
      console.warn('[klaviyo-newsletter] Missing publicKey or listId. publicKey=%s listId=%s locale=%s', publicKey, listId, locale);
      return;
    }

    // Clear validation error as soon as user starts fixing it
    attachClearError(nameInput, 'input');
    attachClearError(emailInput, 'input');
    attachClearError(consentInput, 'change');

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      // Honeypot: silently drop bot submissions
      var honeypot = form.querySelector('input[name="website"]');
      if (honeypot && honeypot.value.trim() !== '') return;

      // Time guard: refuse if user submitted faster than humanly possible
      if (Date.now() - loadTime < MIN_TIME_ON_PAGE_MS) return;

      hide(successEl);
      hide(errorEl);

      var data       = new FormData(form);
      var email      = String(data.get('contact[email]') || data.get('email') || '').trim();
      var firstName  = String(data.get('contact[first_name]') || data.get('first_name') || '').trim();

      // Field-level validation in submit order — first failure wins
      if (nameInput && nameInput.required && !firstName) {
        return showError(msgs.nameMissing, nameInput);
      }
      if (emailInput && emailInput.required && !email) {
        return showError(msgs.emailMissing, emailInput);
      }
      if (emailInput && !emailInput.checkValidity()) {
        return showError(msgs.emailInvalid, emailInput);
      }
      if (consentInput && consentInput.required && !consentInput.checked) {
        return showError(msgs.consentMissing, consentInput);
      }

      var profileAttrs = {
        email: email,
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
      };
      if (firstName) profileAttrs.first_name = firstName;
      if (klaviyoLocale) profileAttrs.locale = klaviyoLocale;
      // `locale` here is the bare language code (pl / en), not klaviyoLocale's
      // language-COUNTRY form — the flows compare against exactly this.
      if (locale) {
        profileAttrs.properties = profileAttrs.properties || {};
        profileAttrs.properties[PREFERRED_LANGUAGE_PROP] = locale;
      }

      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: source,
            profile: { data: { type: 'profile', attributes: profileAttrs } }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      };

      var submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      // Pre-create the profile so the native `locale` field is set.
      //
      // Klaviyo applies `locale` from the client API only when it CREATES the
      // profile; on an existing profile the field is accepted and ignored. It
      // is also discarded entirely from the subscription payload while a double
      // opt-in is pending. So this call has to land before anything else that
      // could create the profile: the subscription request, and identify().
      //
      // Deliberately non-blocking: any failure here is logged and swallowed, and
      // the subscription proceeds untouched. Consent must never depend on it.
      preCreateProfileWithLocale(publicKey, email, firstName, klaviyoLocale, locale)
        .then(function () {
          return fetch(KLAVIYO_API + '?company_id=' + encodeURIComponent(publicKey), {
            method:  'POST',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              'Revision':     KLAVIYO_API_REVISION
            },
            body: JSON.stringify(payload)
          });
        })
        .then(function (res) {
          // Klaviyo returns 202 Accepted on success
          if (res.status === 202 || res.ok) {
            debugLog('subscription accepted', res.status, 'list=' + listId);
            identifyProfile(email, firstName, klaviyoLocale, locale);
            // Mark form as submitted — CSS rule hides everything except the
            // success block. Also keep the inline-style hide as a fallback
            // in case the CSS doesn't load for some reason.
            form.setAttribute('data-state', 'success');
            if (fieldWrapper) fieldWrapper.style.display = 'none';
            show(successEl);
            form.reset();
          } else {
            return res.text().then(function (body) {
              console.error('[klaviyo-newsletter] Submit failed', res.status, body);
              showError(msgs.submitFailed);
            });
          }
        })
        .catch(function (err) {
          console.error('[klaviyo-newsletter] Network error', err);
          showError(msgs.submitFailed);
        })
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
        });
    });

    function showError(text, focusEl) {
      if (errorTextEl) {
        errorTextEl.textContent = text;
      } else if (errorEl) {
        errorEl.textContent = text; // fallback if no inner span
      }
      show(errorEl);
      if (focusEl && typeof focusEl.focus === 'function') {
        try { focusEl.focus(); } catch (e) { /* swallow */ }
      }
    }

    function attachClearError(el, evt) {
      if (!el) return;
      el.addEventListener(evt, function () { hide(errorEl); });
    }
  }

  /**
   * Tag the anonymous __kla_id cookie with this profile so future tracked
   * events (Viewed Product, Added to Cart, ...) are attributed to the now-
   * identified subscriber. Past anonymous events on this cookie are merged
   * retroactively by Klaviyo (typically within a few minutes).
   *
   * Belt-and-suspenders: pushes via BOTH the modern klaviyo.identify() API
   * and the legacy _learnq queue. They dedupe internally, and this ensures
   * identification works regardless of:
   *   - whether klaviyo.js has finished loading at this moment
   *   - whether the Shopify Klaviyo app embed exposes the modern global
   */
  /**
   * Create the profile with its native `locale` set, before anything else can
   * create it. Uses Klaviyo's public-key client profiles endpoint.
   *
   * Verified behaviour (2026-08-07), which is why this runs first and alone:
   *   - on a NEW profile, `locale` is applied to the native field
   *   - on an EXISTING profile, `locale` is accepted and silently ignored
   *   - inside a subscription payload on a double opt-in list, the whole
   *     profile object is discarded until the opt-in is confirmed
   *   - via klaviyo.identify(), `locale` lands in custom properties instead
   *
   * Always resolves. A failure here must never block the subscription, so the
   * promise is deliberately un-rejectable.
   */
  function preCreateProfileWithLocale(publicKey, email, firstName, klaviyoLocale, lang) {
    // Proceed if EITHER value is present. `Preferred Language` is the one the
    // flows read, so a missing/malformed klaviyoLocale must no longer skip the
    // call — otherwise the property would never land at creation time.
    if (!klaviyoLocale && !lang) {
      debugLog('no locale or language resolved, skipping pre-create');
      return Promise.resolve();
    }
    var attrs = { email: email };
    if (klaviyoLocale) attrs.locale = klaviyoLocale;
    if (firstName) attrs.first_name = firstName;
    if (lang) {
      attrs.properties = {};
      attrs.properties[PREFERRED_LANGUAGE_PROP] = lang;
    }

    debugLog('pre-create profile', JSON.stringify(attrs));

    return fetch(KLAVIYO_PROFILES_API + '?company_id=' + encodeURIComponent(publicKey), {
      method:  'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'Revision':     KLAVIYO_API_REVISION
      },
      body: JSON.stringify({ data: { type: 'profile', attributes: attrs } })
    })
      .then(function (res) {
        debugLog('pre-create response', res.status);
        if (!(res.status === 202 || res.ok)) {
          return res.text().then(function (b) {
            console.warn('[klaviyo-newsletter] locale pre-create returned %s: %s', res.status, b);
          });
        }
      })
      .catch(function (err) {
        console.warn('[klaviyo-newsletter] locale pre-create failed (continuing)', err);
      });
  }

  function identifyProfile(email, firstName, klaviyoLocale, lang) {
    var props = { email: email };
    if (firstName) props.first_name = firstName;
    // Record the locale the subscriber actually signed up in, as a custom
    // property distinct from the native `locale` field.
    //
    // identify() cannot write the native field — anything it doesn't recognise
    // lands in custom properties — so this is deliberately a SEPARATE value, not
    // a second attempt at the same one. preCreateProfileWithLocale() owns the
    // native field; this is the first-touch record, which stays put even if the
    // native locale is later rewritten by the Shopify sync or another integration.
    if (klaviyoLocale) props[INITIAL_LOCALE_PROP] = klaviyoLocale;
    // Custom properties are exactly what identify() CAN write, so this is the
    // reliable path for `Preferred Language` — including on profiles Klaviyo
    // has already seen, where the pre-create call is ignored.
    if (lang) props[PREFERRED_LANGUAGE_PROP] = lang;

    var legacy = { '$email': email };
    if (firstName) legacy['$first_name'] = firstName;
    if (klaviyoLocale) legacy[INITIAL_LOCALE_PROP] = klaviyoLocale;
    if (lang) legacy[PREFERRED_LANGUAGE_PROP] = lang;

    // 1) Legacy queue — works whether klaviyo.js is loaded or not.
    //    klaviyo.js drains this queue on load.
    window._learnq = window._learnq || [];
    window._learnq.push(['identify', legacy]);

    // 2) Modern global — only available once klaviyo.js has loaded.
    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
      try { window.klaviyo.identify(props); } catch (e) { /* swallow */ }
    }
  }

  function show(el) { if (el) el.removeAttribute('hidden'); }
  function hide(el) { if (el) el.setAttribute('hidden', ''); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
